/**
 * Role Prioritisation, section 03 — lend part of a recruiter's week to another cluster, then commit
 * that arrangement until the next cluster-lead weekly.
 *
 * The unit is a recruiter hour. The PERSON is named because support is a commitment somebody has to
 * keep; the arithmetic itself stays cluster-level, which is why `focus` (which rating band the
 * support covers) is recorded as intent for the receiving lead and never fed into the maths.
 *
 * Nothing here writes to Airtable, Lever or the capacity sheet. A support allocation is an internal
 * TA arrangement, not a change of role ownership.
 */
import { useMemo, useState } from 'react';
import { Badge } from '@finn-auto/design-system/atoms/badge';
import { Button } from '@finn-auto/design-system/atoms/button';
import {
  RATINGS,
  type Allocation,
  type CapacityAssumptions,
  type EffortModel,
  type PrioritisationState,
  type Rating,
} from '../../../types/rolePrioritisation';
import {
  hoursLabel,
  hoursLentBy,
  pctLabel,
  personCapacity,
  tierHours,
  validateAllocation,
  type ClusterStats,
} from '../../../utils/rolePrioritisation';
import { Card, SectionHead, hoursText } from './prioritisationUi';
import { CapacityLevers } from './CapacityLevers';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-iron">{label}</span>
      {children}
    </label>
  );
}

export function SupportBoard({
  stats,
  state,
  model,
  cap,
  weeklyLabel,
  expired,
  onStateChange,
  onSuggest,
  onCommit,
  onRevise,
  onClear,
  warnings,
}: {
  stats: ClusterStats[];
  state: PrioritisationState;
  model: EffortModel;
  cap: CapacityAssumptions;
  weeklyLabel: string;
  /** The stored commitment's own validity date has passed, so it no longer locks the board. */
  expired: boolean;
  onStateChange: (next: PrioritisationState) => void;
  onSuggest: () => string | null;
  onCommit: () => void;
  onRevise: () => void;
  onClear: () => void;
  warnings: { overLent: string[]; donorsOver: string[] };
}) {
  // An expired commitment must not keep the board read-only: it would present an arrangement whose
  // validity date has passed as though it were still in force, and the only way back would be to
  // notice the stale date and press Revise.
  const locked = !!state.commitment && !expired;
  const overs = useMemo(() => stats.filter((c) => c.spare < -0.005), [stats]);

  // Opens on the case a lead actually has: the cluster that is over, supported by the person with
  // the longest week in the LEAST-stretched cluster that has room.
  const defaults = useMemo(() => {
    const target = overs[0]?.key ?? stats[0]?.key ?? '';
    const donor = stats
      .filter((c) => c.key !== target && c.spare > 0)
      .sort((a, b) => (a.utilisation ?? 0) - (b.utilisation ?? 0))[0];
    const person = state.team
      .map((p, i) => ({ p, i }))
      .filter((e) => e.p.cluster === donor?.key && e.p.hours > 0)
      .sort((a, b) => b.p.hours - a.p.hours)[0];
    return { target, from: person ? String(person.i) : '' };
  }, [overs, stats, state.team]);

  const [hours, setHours] = useState('2.5');
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.target);
  const [focus, setFocus] = useState<Rating | ''>('');
  const [message, setMessage] = useState<{ text: string; bad: boolean } | null>(null);

  const byCluster = useMemo(() => {
    const map = new Map<string, { name: string; index: number; hours: number; planned: boolean }[]>();
    state.team.forEach((p, index) => {
      if (p.hours <= 0) return;
      const list = map.get(p.cluster) ?? [];
      // Someone not planned as capacity is still offerable here — that IS how a lead lends time.
      list.push({ name: p.name, index, hours: p.hours, planned: p.countsTowardHiring !== false });
      map.set(p.cluster, list);
    });
    return map;
  }, [state.team]);

  function add() {
    const parsed = parseFloat(hours);
    const fromIndex = parseInt(from, 10);
    const problem = validateAllocation(state, fromIndex, to, parsed);
    if (problem) { setMessage({ text: problem, bad: true }); return; }
    onStateChange({
      ...state,
      allocations: [...state.allocations, { from: fromIndex, to, hours: parsed, focus }],
    });
    // Reset the source: leaving it filled read as "one form, one allocation", and clicking Add
    // again with the same person tripped the duplicate-allocation validation.
    setFrom('');
    setFocus('');
    setMessage({
      text: `Added — ${hoursText(parsed)} of ${state.team[fromIndex].name} to ${to}. Pick another person to add more.`,
      bad: false,
    });
  }

  return (
    <section>
      <SectionHead
        number="03"
        title="Balance the load"
        aside="Every capacity what-if lives here · commit until the next weekly"
      />
      <Card>
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 border-b border-solid border-pearl px-4 py-3 text-xs text-iron">
          <span className="text-sm font-semibold text-black">Support allocations</span>
          {locked ? (
            <Badge variant="blue">Committed · to {state.commitment?.validUntil}</Badge>
          ) : expired ? (
            <Badge variant="orange">Expired · was to {state.commitment?.validUntil}</Badge>
          ) : (
            <Badge variant={state.allocations.length ? 'grey' : 'monochromeBlue'}>
              {state.allocations.length
                ? `Draft · ${state.allocations.length}`
                : 'Draft · nothing allocated'}
            </Badge>
          )}
          <span>
            {locked
              ? 'Agreed at the cluster-lead weekly. Read-only until revised.'
              : expired
                ? 'That arrangement has run out — re-cut it at this week’s weekly and commit again.'
                : overs.length
                  ? overs.map((c) => `${c.key} over by ${hoursText(-c.spare)}`).join(' · ')
                  : 'Every cluster is within its capacity.'}
          </span>
          <span className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={locked}
              onClick={() => setMessage({ text: onSuggest() ?? '', bad: false })}
            >
              Suggest
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={locked || state.allocations.length === 0}
              onClick={onCommit}
            >
              Commit until {weeklyLabel}
            </Button>
            {locked ? (
              <Button variant="secondary" size="sm" onClick={onRevise}>
                Revise
              </Button>
            ) : null}
            {/* Disabled while committed: the board states the plan is read-only until Revise, and
                Clear would otherwise delete the commitment and every lever behind that notice. */}
            <Button variant="secondary" size="sm" disabled={locked} onClick={onClear}>
              Clear
            </Button>
          </span>
        </div>

        <CapacityLevers
          stats={stats}
          state={state}
          cap={cap}
          locked={locked}
          onStateChange={onStateChange}
        />

        {locked ? (
          <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1.5 border-b border-solid border-pearl bg-lightBlue px-4 py-3 text-sm text-[#0B4F9E]">
            <b className="font-semibold">In effect until {state.commitment?.validUntil}</b>
            <span>
              committed by {state.commitment?.by} · {state.commitment?.at}
            </span>
            <span>reviewed and re-cut at the next cluster-lead weekly</span>
          </div>
        ) : (
          /*
            A LABELLED FORM, not a sentence.
            The first version read "Assign [n] h / week of [who] → [where] for [what]" as one line of
            prose with controls embedded in it. At real widths that wrapped between the words and the
            fields — the number input ended up orphaned on its own line above "h / week of" — and the
            control stopped being legible as a control at all. Four labelled fields on a grid cannot
            garble, whatever the width.
          */
          <div className="border-b border-solid border-pearl bg-cotton px-4 py-3">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Hours / week">
                <input
                  type="number"
                  min={0.5}
                  max={40}
                  step={0.5}
                  value={hours}
                  aria-label="Hours per week"
                  onChange={(e) => setHours(e.target.value)}
                  className="h-9 w-[92px] rounded-md border border-solid border-pewter bg-white px-2 text-right text-sm tabular-nums text-black hover:border-iron focus:outline-none focus:ring-2 focus:ring-trustedBlue"
                />
              </Field>

              <Field label="From">
                <select
                  value={from}
                  aria-label="Recruiter providing support"
                  onChange={(e) => setFrom(e.target.value)}
                  className="h-9 w-[210px] rounded-md border border-solid border-pewter bg-white px-2 text-sm text-black hover:border-iron focus:outline-none focus:ring-2 focus:ring-trustedBlue"
                >
                  <option value="">Pick a recruiter…</option>
                  {stats.map((c) => (
                    <optgroup key={c.key} label={c.key}>
                      {(byCluster.get(c.key) ?? []).map((p) => (
                        <option key={p.index} value={String(p.index)}>
                          {p.name} · {p.hours} h{p.planned ? '' : ' · not planned'}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>

              <span className="pb-2 text-iron">→</span>

              <Field label="To support">
                <select
                  value={to}
                  aria-label="Cluster receiving support"
                  onChange={(e) => setTo(e.target.value)}
                  className="h-9 w-[168px] rounded-md border border-solid border-pewter bg-white px-2 text-sm text-black hover:border-iron focus:outline-none focus:ring-2 focus:ring-trustedBlue"
                >
                  {stats.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.key}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="For">
                <select
                  value={focus}
                  aria-label="Which work the support covers"
                  onChange={(e) => setFocus(e.target.value as Rating | '')}
                  className="h-9 w-[140px] rounded-md border border-solid border-pewter bg-white px-2 text-sm text-black hover:border-iron focus:outline-none focus:ring-2 focus:ring-trustedBlue"
                >
                  <option value="">any work</option>
                  {RATINGS.map((r) => (
                    <option key={r} value={r}>
                      {r} roles
                    </option>
                  ))}
                </select>
              </Field>

              <button
                type="button"
                onClick={add}
                disabled={from === ''}
                className="h-9 rounded-md bg-black px-4 text-sm font-medium text-white hover:bg-[#000] focus:outline-none focus:ring-2 focus:ring-trustedBlue disabled:cursor-not-allowed disabled:opacity-40"
              >
                {state.allocations.length > 0 ? '+ Add another' : 'Add support'}
              </button>
            </div>
            {message ? (
              <div
                className={
                  message.bad ? 'mt-2 text-xs font-medium text-[#8A0E22]' : 'mt-2 text-xs text-iron'
                }
              >
                {message.text}
              </div>
            ) : null}
          </div>
        )}

        {state.allocations.length === 0 ? (
          <div className="px-4 py-3.5 text-[13px] text-iron">
            No support allocated.{' '}
            {overs.length ? 'Add one above, or press Suggest.' : 'Nothing needs balancing right now.'}
          </div>
        ) : (
          <>
            {(() => {
              const committedIdx: number[] = [];
              const draftIdx: number[] = [];
              state.allocations.forEach((a, i) => {
                if (a.committedAt) committedIdx.push(i);
                else draftIdx.push(i);
              });
              return (
                <>
                  {committedIdx.length > 0 ? (
                    <SubHead
                      label="Committed last week — carrying over"
                      note="Reset a row to drop it before the next weekly, or confirm again with the bulk button below."
                    />
                  ) : null}
                  {committedIdx.map((idx) =>
                    renderRow(idx, {
                      state,
                      cap,
                      model,
                      locked,
                      onStateChange,
                      variant: 'committed',
                    })
                  )}
                  {draftIdx.length > 0 ? (
                    <SubHead
                      label={
                        committedIdx.length > 0 ? 'New this week' : 'Draft — nothing confirmed yet'
                      }
                      note={
                        committedIdx.length > 0
                          ? 'Confirm a row to carry it over, or leave it as a scratchpad move.'
                          : 'Confirm rows individually, or use Commit until the next weekly to confirm the whole plan.'
                      }
                    />
                  ) : null}
                  {draftIdx.map((idx) =>
                    renderRow(idx, {
                      state,
                      cap,
                      model,
                      locked,
                      onStateChange,
                      variant: 'draft',
                    })
                  )}
                </>
              );
            })()}
          </>
        )}

        {/*
          Open decision 3 — support allocations are stored per-BROWSER in localStorage. Every cluster
          lead sees their own board. Saying so on the section itself is the pragmatic answer while
          server-side storage is not built: an invisible per-browser scope reads as a bug the first
          time two leads compare screens, and the banner tells them why before they ask.
        */}
        <div className="flex items-baseline gap-2 border-t border-solid border-pearl bg-cotton px-4 py-2 text-[11px] text-iron">
          <span className="font-semibold text-asphalt">Per-browser only —</span>
          <span>
            support allocations save on your Mac. Other cluster leads see their own board until this
            moves server-side (open decision 3).
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-solid border-pearl px-4 py-3 text-xs tabular-nums text-iron">
          {warnings.overLent.length ? (
            <span className="flex items-center gap-2">
              <Badge variant="monochrome">Over their week</Badge>
              {warnings.overLent.join(', ')}
            </span>
          ) : null}
          {warnings.donorsOver.length ? (
            <span className="flex items-center gap-2">
              <Badge variant="monochrome">Donor pushed over</Badge>
              {warnings.donorsOver.join(', ')} — the gap has moved, not closed
            </span>
          ) : null}
          {!warnings.overLent.length && !warnings.donorsOver.length ? (
            <span>
              {state.allocations.length
                ? 'Every lender is inside their own week and no donor was pushed over.'
                : 'Nothing committed yet. A committed plan is visible to everyone who can read this tab.'}
            </span>
          ) : null}
        </div>
      </Card>
    </section>
  );
}

/** Group heading used to split committed rows from drafts. */
function SubHead({ label, note }: { label: string; note?: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-solid border-pearl bg-snow px-4 py-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-iron">{label}</span>
      {note ? <span className="text-[11px] text-steel">{note}</span> : null}
    </div>
  );
}


/**
 * One allocation row. `variant` picks the per-row actions: committed → Reset (removes just that
 * row); draft → Confirm (stamps committedAt) · Remove. `locked` (whole-plan Commit-until-weekly)
 * still hides every row action, preserving the earlier "Clear disabled without Revise" behaviour.
 */
function renderRow(
  idx: number,
  {
    state,
    cap,
    model,
    locked,
    onStateChange,
    variant,
  }: {
    state: PrioritisationState;
    cap: CapacityAssumptions;
    model: EffortModel;
    locked: boolean;
    onStateChange: (next: PrioritisationState) => void;
    variant: 'committed' | 'draft';
  }
) {
  const a = state.allocations[idx];
  const person = state.team[a.from];
  const own = personCapacity(person, cap);
  const lent = hoursLentBy(state, a.from);
  function replaceAt(next: Allocation) {
    onStateChange({
      ...state,
      allocations: state.allocations.map((x, i) => (i === idx ? next : x)),
    });
  }
  function removeAt() {
    onStateChange({ ...state, allocations: state.allocations.filter((_, i) => i !== idx) });
  }
  return (
    <div
      key={`${a.from}-${a.to}-${idx}`}
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-solid border-pearl px-4 py-2.5 text-[13px] tabular-nums last:border-b-0"
    >
      <span className="font-semibold text-black">{person.name}</span>
      <span className="text-xs text-iron">
        {person.cluster} → <b className="font-semibold text-black">{a.to}</b>
        {a.focus ? ` · ${a.focus} roles` : ' · any work'}
      </span>
      {variant === 'committed' ? (
        <Badge variant="grey">carrying over</Badge>
      ) : null}
      <span className="ml-auto font-semibold text-black">{hoursText(a.hours)} / week</span>
      <span className="text-xs text-iron">
        {own > 0 ? `${pctLabel((a.hours / own) * 100)} of their ${hoursText(own)}` : '—'}
        {a.focus
          ? ` · covers ${hoursLabel(a.hours / tierHours(model, a.focus))} of an ${a.focus} pipeline`
          : ''}
      </span>
      {lent > own + 0.005 ? <Badge variant="monochrome">over their week</Badge> : null}
      {locked ? null : variant === 'draft' ? (
        <>
          <Button variant="secondary" size="sm" aria-label={`Confirm support from ${person.name}`}
            onClick={() => replaceAt({ ...a, committedAt: new Date().toISOString() })}>Confirm</Button>
          <Button variant="ghost" size="sm" aria-label={`Remove support from ${person.name}`}
            onClick={removeAt}>Remove</Button>
        </>
      ) : (
        <Button variant="ghost" size="sm" aria-label={`Reset support from ${person.name}`}
          onClick={removeAt}>Reset</Button>
      )}
    </div>
  );
}
